import MessageService from '@/services/MessageService';
import io from 'socket.io-client';

const state = {
    messages: [],
    unreadCount: 0,
    popupMessages: [],
    loading: false,
    error: null,
    socket: null,
    filters: {
        importance: null,
        unreadOnly: false,
        searchTerm: ''
    }
};

const getters = {
    allMessages: state => state.messages || [],
    unreadCount: state => state.unreadCount || 0,
    popupMessages: state => state.popupMessages || [],
    isLoading: state => state.loading,
    hasError: state => state.error,
    messagesByImportance: state => importance => {
        if (!importance) return state.messages || [];
        return (state.messages || []).filter(m => m.importance === importance);
    },
    unreadMessages: state => (state.messages || []).filter(m => !m.isRead)
};

const actions = {
    // Initialize socket connection
    initSocket({ commit, dispatch }, user) {
        // Extract user information
        // IMPORTANT: Use numeric ID (user.id) to match server-side logic in PacklistController
        // user.id = numeric ID from auth table (e.g., 10)
        // user.user = username string (e.g., "christopher")
        const userId = user?.id || user?._id || localStorage.getItem('userId') || sessionStorage.getItem('userId');
        const username = user?.user || user?.name || user?.username || 'Unknown';
        const userRole = user?.role || 'user';
        
        // Use the same connection pattern as internal-messages
        // Connect to namespace without specifying URL (uses current origin)
        console.log('[Socket.io] Attempting to connect to /messages namespace');
        console.log('[Socket.io] With user data:', { userId, username, userRole });
        
        // Use relative path without specifying host/port to avoid SSL issues
        // This will connect to the same host as the page was loaded from
        const socket = io('/messages', {
            auth: {
                userId: userId,
                username: username,
                role: userRole
            },
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 1000,
            timeout: 20000,
            transports: ['polling', 'websocket']  // Start with polling for better compatibility
        });

        socket.on('connect', () => {
            console.log('[Socket.io] ✅ Connected to messages namespace');
            console.log('[Socket.io] Socket ID:', socket.id);
            console.log('[Socket.io] User ID:', userId);
        });
        
        socket.on('connect_error', (error) => {
            console.error('[Socket.io] ❌ Connection error:', error.message);
            console.error('[Socket.io] Error type:', error.type);
        });
        
        socket.on('disconnect', (reason) => {
            console.log('[Socket.io] ⚠️ Disconnected:', reason);
        });
        
        socket.on('reconnect', (attemptNumber) => {
            console.log('[Socket.io] 🔄 Reconnected after', attemptNumber, 'attempts');
        });

        socket.on('new-message', (message) => {
            console.log('[Socket.io] 📨 Received new message:', message);
            commit('ADD_MESSAGE', message);
            dispatch('fetchUnreadCount');
            
            // Add to popup messages if showAsPopup is true and not already read
            if (message.showAsPopup && !message.readBy?.some(r => r.userId === userId)) {
                console.log('[Socket.io] Message has showAsPopup=true, adding to popupMessages');
                commit('ADD_POPUP_MESSAGE', message);
                // Small delay to ensure DOM updates
                setTimeout(() => {
                    commit('TRIGGER_POPUP_DISPLAY');
                }, 100);
            }
            
            // Show notification for important messages
            if (message.importance === 'high' || message.importance === 'critical') {
                dispatch('showNotification', message);
            }
        });

        socket.on('unread-count-update', ({ count }) => {
            console.log('[Socket.io] 🔢 Unread count update:', count);
            commit('SET_UNREAD_COUNT', count || 0);
        });

        socket.on('message-deleted', ({ messageId }) => {
            console.log('[Socket.io] 🗑️ Message deleted:', messageId);
            commit('REMOVE_MESSAGE', messageId);
        });

        commit('SET_SOCKET', socket);
        console.log('[Socket.io] Socket instance stored in Vuex');
    },

    // Fetch messages
    async fetchMessages({ commit, state }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        
        try {
            const { data } = await MessageService.getMessages(state.filters);
            if (data.success) {
                commit('SET_MESSAGES', data.messages);
            }
        } catch (error) {
            commit('SET_ERROR', error.message);
        } finally {
            commit('SET_LOADING', false);
        }
    },

    // Fetch unread count
    async fetchUnreadCount({ commit }) {
        try {
            const { data } = await MessageService.getUnreadCount();
            if (data.success) {
                commit('SET_UNREAD_COUNT', data.count);
            }
        } catch (error) {
            console.error('Error fetching unread count:', error);
        }
    },

    // Mark message as read
    async markAsRead({ commit, dispatch }, messageId) {
        try {
            const { data } = await MessageService.markAsRead(messageId);
            if (data.success) {
                commit('UPDATE_MESSAGE_READ_STATUS', messageId);
                dispatch('fetchUnreadCount');
            }
        } catch (error) {
            console.error('Error marking message as read:', error);
        }
    },

    // Fetch popup messages
    async fetchPopupMessages({ commit }) {
        console.log('[Store] fetchPopupMessages action called');
        try {
            const { data } = await MessageService.getPopupMessages();
            console.log('[Store] API response:', data);
            if (data.success) {
                commit('SET_POPUP_MESSAGES', data.messages);
                // Trigger immediate display of unread popup messages
                if (data.messages && data.messages.length > 0) {
                    console.log('[Store] Triggering popup display for', data.messages.length, 'messages');
                    commit('TRIGGER_POPUP_DISPLAY');
                }
            }
        } catch (error) {
            console.error('Error fetching popup messages:', error);
        }
    },

    // Create message (Admin)
    async createMessage({ dispatch }, messageData) {
        try {
            const { data } = await MessageService.createMessage(messageData);
            if (data.success) {
                dispatch('fetchMessages');
                return data.message;
            }
        } catch (error) {
            throw error;
        }
    },

    // Delete message (Admin)
    async deleteMessage({ dispatch }, messageId) {
        try {
            const { data } = await MessageService.deleteMessage(messageId);
            if (data.success) {
                dispatch('fetchMessages');
            }
        } catch (error) {
            throw error;
        }
    },

    // Update filters
    updateFilters({ commit, dispatch }, filters) {
        commit('SET_FILTERS', filters);
        dispatch('fetchMessages');
    },

    // Show notification
    showNotification({ commit }, message) {
        if ('Notification' in window && Notification.permission === 'granted') {
            new Notification(message.title, {
                body: message.content.replace(/<[^>]*>/g, '').substring(0, 100) + '...',
                icon: '/favicon.ico',
                tag: message._id
            });
        }
    },

    // Cleanup socket
    cleanup({ state, commit }) {
        if (state.socket) {
            state.socket.disconnect();
            commit('SET_SOCKET', null);
        }
    }
};

const mutations = {
    SET_MESSAGES(state, messages) {
        state.messages = messages;
    },
    
    TRIGGER_POPUP_DISPLAY(state) {
        console.log('[Store] TRIGGER_POPUP_DISPLAY called');
        // Force Vue to detect changes by creating a new array reference
        state.popupMessages = [...state.popupMessages];
        console.log('[Store] popupMessages after trigger:', state.popupMessages);
    },
    
    ADD_MESSAGE(state, message) {
        state.messages.unshift(message);
    },
    
    REMOVE_MESSAGE(state, messageId) {
        state.messages = state.messages.filter(m => m._id !== messageId);
    },
    
    UPDATE_MESSAGE_READ_STATUS(state, messageId) {
        const message = state.messages.find(m => m._id === messageId);
        if (message) {
            message.isRead = true;
        }
        // Also remove from popup messages when marked as read
        state.popupMessages = state.popupMessages.filter(m => m._id !== messageId);
    },
    
    SET_UNREAD_COUNT(state, count) {
        state.unreadCount = count;
    },
    
    SET_POPUP_MESSAGES(state, messages) {
        console.log('[Store] SET_POPUP_MESSAGES called with:', messages);
        state.popupMessages = messages;
        console.log('[Store] popupMessages state after set:', state.popupMessages);
    },
    
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    
    SET_ERROR(state, error) {
        state.error = error;
    },
    
    SET_SOCKET(state, socket) {
        state.socket = socket;
    },
    
    SET_FILTERS(state, filters) {
        state.filters = { ...state.filters, ...filters };
    },
    
    ADD_POPUP_MESSAGE(state, message) {
        if (!state.popupMessages.find(m => m._id === message._id)) {
            // Use spread operator to create new array reference for proper reactivity
            state.popupMessages = [...state.popupMessages, message];
            console.log('[Store] ADD_POPUP_MESSAGE added message:', message._id);
            console.log('[Store] Current popupMessages count:', state.popupMessages.length);
        }
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};